import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ConfirmDialog from './ConfirmDialog';
import SnackbarAlert from './SnackbarAlert';
import { useSnackbar } from '../hooks/useSnackbar';
import { useAwsAccountService } from '../../features/aws-account/services/awsAccountService';
import { extractErrorMessage } from '../utils/errorUtils';

export interface AWSConsoleButtonProps {
  customerId: string;
  accountId: string;
  region: string;
  allowWrite?: boolean;
  disabled?: boolean;
}

const AWSConsoleButton: React.FC<AWSConsoleButtonProps> = ({
  customerId,
  accountId,
  region,
  allowWrite = false,
  disabled = false,
}) => {
  const awsAccountService = useAwsAccountService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmWriteDialog, setConfirmWriteDialog] = useState(false);

  const destination = `https://console.aws.amazon.com/console/home?region=${region}`;

  const handleOpenConsole = useCallback(async (roleType: 'readonly' | 'write' = 'readonly') => {
    setLoading(true);
    try {
      const result = await awsAccountService.getConsoleAccess(
        customerId,
        accountId,
        roleType,
        destination
      );
      window.open(result.signin_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showSnackbar(extractErrorMessage(error, 'Failed to generate console access URL'), 'error');
    } finally {
      setLoading(false);
    }
  }, [customerId, accountId, destination, awsAccountService, showSnackbar]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleReadonlyAccess = useCallback(() => {
    handleOpenConsole('readonly');
    handleMenuClose();
  }, [handleOpenConsole, handleMenuClose]);

  const handleWriteAccessRequest = useCallback(() => {
    setConfirmWriteDialog(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleWriteAccessConfirm = useCallback(() => {
    handleOpenConsole('write');
    setConfirmWriteDialog(false);
  }, [handleOpenConsole]);

  if (!allowWrite) {
    return (
      <>
        <Button
          size="small"
          variant="text"
          onClick={() => handleOpenConsole('readonly')}
          disabled={loading || disabled}
          sx={{
            minWidth: 'auto',
            px: 0.75,
            py: 0.25,
            fontSize: '0.7rem',
            color: 'text.secondary',
            '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
          }}
          startIcon={
            loading ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            )
          }
        >
          Console
        </Button>
        <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} autoHideDuration={6000} />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <Button
          size="small"
          variant="text"
          onClick={() => handleOpenConsole('readonly')}
          disabled={loading || disabled}
          sx={{
            minWidth: 'auto',
            px: 0.75,
            py: 0.25,
            fontSize: '0.7rem',
            color: 'text.secondary',
            borderRadius: '4px 0 0 4px',
            '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
          }}
          startIcon={
            loading ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            )
          }
        >
          Console
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={handleMenuOpen}
          disabled={loading || disabled}
          sx={{
            minWidth: 'auto',
            px: 0.25,
            py: 0.25,
            color: 'text.secondary',
            borderRadius: '0 4px 4px 0',
            '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
          }}
        >
          <ArrowDropDownIcon sx={{ fontSize: 16 }} />
        </Button>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleReadonlyAccess}>
          <Typography variant="body2">Readonly Access</Typography>
        </MenuItem>
        <MenuItem onClick={handleWriteAccessRequest}>
          <Typography variant="body2" color="warning.main">
            Write Access
          </Typography>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={confirmWriteDialog}
        title="Write Access Warning"
        onCancel={() => setConfirmWriteDialog(false)}
        onConfirm={handleWriteAccessConfirm}
        confirmText="OK - Open with Write Access"
        confirmColor="warning"
      >
        You are about to open the AWS Console with <strong>write permissions</strong>.
        <br />
        <br />
        This allows you to make changes to AWS resources. Are you sure you want to proceed?
      </ConfirmDialog>

      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} autoHideDuration={6000} />
    </>
  );
};

export default AWSConsoleButton;
